import assertEventType from 'common/bento/lib/assertEventType';
import { MachineServices } from 'common/bento/types/MachineConfig';
import { apolloClient } from 'common/graphql/clients';

import { ApplicationType as StartApplicationType } from '../../../__generated__/GQL';
import { restartGraphQLSubscriptionsWebSocket } from '../../../common/graphql/utils';
import { StartApplicationDocument } from '../graphql/mutations/startApplication.gql';
import { OnboardingUserDocument } from '../graphql/queries/onboardingUser.gql';
import { ApplicationType } from '../types';
import model, { Model } from './model';

type ApplicationTypeEvent = Extract<
  keyof typeof model.events,
  | 'SELECT_CAPITAL_DEPOSIT'
  | 'SELECT_SHINE_START'
  | 'SELECT_STANDARD'
  | 'SELECT_SASU_CREATION'
>;

const EVENT_TO_APPLICATION_TYPE: Record<ApplicationTypeEvent, ApplicationType> =
  {
    SELECT_CAPITAL_DEPOSIT: ApplicationType.CapitalDeposit,
    SELECT_SASU_CREATION: ApplicationType.SasuCreation,
    SELECT_SHINE_START: ApplicationType.ShineStart,
    SELECT_STANDARD: ApplicationType.Standard,
  };

const EVENT_TO_START_APPLICATION_TYPE: Record<
  ApplicationTypeEvent,
  StartApplicationType
> = {
  SELECT_CAPITAL_DEPOSIT: StartApplicationType.CapitalDeposit,
  SELECT_SASU_CREATION: StartApplicationType.CompanyCreationSasu,
  SELECT_SHINE_START: StartApplicationType.ShineStart,
  SELECT_STANDARD: StartApplicationType.Standard,
};

export enum Service {
  CreateNewAccount = 'createNewAccount',
  StartApplication = 'startApplication',
}

const services: MachineServices<Model, Service> = {
  createNewAccount: async ({ getModuleData }) =>
    getModuleData().createNewAccount(),

  startApplication: async ({ getModuleData }, event) => {
    assertEventType(event, [
      'SELECT_CAPITAL_DEPOSIT',
      'SELECT_SHINE_START',
      'SELECT_STANDARD',
      'SELECT_SASU_CREATION',
    ]);

    getModuleData().applicationTypeContext.dispatch(
      EVENT_TO_APPLICATION_TYPE[event.type],
    );

    const { data } = await apolloClient.mutate({
      mutation: StartApplicationDocument,
      refetchQueries: [OnboardingUserDocument],
      variables: {
        input: {
          type: EVENT_TO_START_APPLICATION_TYPE[event.type],
        },
      },
    });

    if (!data) {
      throw new Error('No data returned from startApplication mutation');
    }

    getModuleData().companyContext.setCompanyContext({
      companyProfileId: data.startApplication.companyProfileId,
      companyUserId: undefined,
    });

    // Restart the websocket connection to ensure GraphQL subscriptions will
    // work with the new companyProfileId
    restartGraphQLSubscriptionsWebSocket();
  },
};

export default services;
